/**
 * 官媒舱成稿预览 / 交付页(R2-T5,照 dept-reporter-preview.html):
 * 左 = 公众号级排版文章(封面 / meta 含 AIGC 标识 / 引言 / 插图带图注 / 标签 / 品牌页脚);
 * 右 = 预检状态 + 配图缩略 + 交付(Word / Markdown / 复制公众号格式 / 转长图禁用 / 发布中心 soon)。
 *
 * ═══ 口径 ═════════════════════════════════════════════════════════════════════
 * · 预检走 R1 真端点 POST /articles/{id}/export-preflight:blocking 拦死 / confirmation
 *   需显式确认(绕过落审计,弹确认框逐条给用户看)/ warning 只提示。
 * · 品牌页脚吃**已有企业配置**:租户名(登录态)+ 电话/邮箱(品牌资产,STAFF 可读);
 *   官网/备案没有配置源就不渲染 —— 不编造占位假信息。
 * · 插图插入位通宵版=固定「封面顶部 + 全部插图列在文末」;逐段插排 TODO 白天
 *   (需要 article↔图片的段落级关联落库,见 reporterImages.ts 头注)。
 */
import { maskVendor } from "@/shared/lib/vendorMask";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Check, Copy, Download, FileText, Loader2, Newspaper } from "lucide-react";

import {
  PREVIEW_AIGC_LABEL_TEXT,
  showPreviewAigcLabel,
} from "@/features/reporter/model/aigcLabelVisibility";
import { resolveCompliance } from "@/entities/compliance";
import { Button } from "@/shared/ui/button";
import { CenteredLoader } from "@/shared/ui/QueryState";
import { useAuth } from "@/contexts/AuthContext";
import { useBrandAssets } from "@/entities/brand-asset";
import { useMutationWithToast } from "@/shared/hooks/useMutationWithToast";
import { useConfirm } from "@/shared/hooks/use-confirm";
import { cn } from "@/shared/lib/utils";
import { reporterGenreOfChannel } from "@/entities/scene-card/model/presets";
import { posterService } from "@/features/poster/api/posterService";
import { reporterService } from "@/features/reporter/api/reporterService";
import { recallSiteTarget } from "@/features/reporter/model/sitePublication";
import { SitePublicationPanel } from "@/features/reporter/ui/SitePublicationPanel";
import { GENRE_LABEL, gateLabel, humanizeReporterText } from "@/features/reporter/model/labels";
import {
  parseImageSuggestions,
  parseSections,
  parseTags,
  parseTitles,
} from "@/features/reporter/model/parse";
import {
  countWords,
  imageSlotsOf,
  matchTasksToSlots,
} from "@/features/reporter/model/reporterImages";

export default function ReporterPreviewPage() {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { user, permissions } = useAuth();
  // 1011 算法备案:标题下「AI 生成内容标识」默认显示,租户 compliance.visibleLabel=false 才隐藏
  const showAigcLabel = showPreviewAigcLabel(
    resolveCompliance(permissions?.compliance).visibleLabel
  );
  const { id } = useParams<{ id: string }>();
  const draftId = Number(id);
  // 1009:从建站舱「新闻栏 → 从新闻舱选稿」进来带 ?site=,发布弹窗预选该站
  const [searchParams] = useSearchParams();
  const presetSiteId = searchParams.get("site") ?? recallSiteTarget();

  const draftQ = useQuery({
    queryKey: ["reporter-draft", draftId],
    queryFn: () => reporterService.getDraft(draftId),
    enabled: Number.isFinite(draftId),
  });
  const draft = draftQ.data;
  const article = draft?.article ?? null;
  const quality = draft?.quality ?? null;

  const sections = useMemo(() => parseSections(article?.sectionsJson), [article?.sectionsJson]);
  const tags = useMemo(() => parseTags(article?.tagsJson), [article?.tagsJson]);
  const titles = useMemo(() => parseTitles(article?.titlesJson), [article?.titlesJson]);
  const suggestions = useMemo(
    () => parseImageSuggestions(article?.imageSuggestionsJson),
    [article?.imageSuggestionsJson]
  );
  const slots = useMemo(() => imageSlotsOf(suggestions), [suggestions]);
  const totalWords = useMemo(
    () => sections.reduce((n, s) => n + countWords(s.content), 0),
    [sections]
  );

  /* ── 预检(页面进来就跑,交付按钮按结果亮灭) ── */
  const preflightQ = useQuery({
    queryKey: ["reporter-preflight", article?.id, article?.currentVersion],
    queryFn: () => reporterService.exportPreflight(article!.id),
    enabled: !!article,
  });
  const pf = preflightQ.data;

  /* ── 配图(与工作台同一名约定回捞) ── */
  const imageTasksQ = useQuery({
    queryKey: ["reporter-images", draftId],
    queryFn: async () => {
      // S2:归属列优先+任务名兜底,服务端一口回捞(租户级,与草稿共享口径一致)
      const page = await posterService.listTasks({
        page: 0,
        size: 20,
        reporterDraftId: draftId,
      });
      return page.records ?? [];
    },
    enabled: Number.isFinite(draftId) && slots.length > 0,
    // E-12:生成中的图位要能自己变过来(与工作台同口径轮询),否则停在「未生成」误导已完成
    refetchInterval: (q) =>
      (q.state.data ?? []).some((t) => t.status === "RUNNING" || t.status === "PENDING")
        ? 3000
        : false,
  });
  const slotTasks = useMemo(
    () => matchTasksToSlots(imageTasksQ.data ?? [], draftId),
    [imageTasksQ.data, draftId]
  );
  const coverUrl = slotTasks["cover"]?.coverThumbUrl ?? null;
  const insertSlots = slots.filter((s) => s.slot !== "cover" && slotTasks[s.slot]?.coverThumbUrl);
  // 批二 P3 正文插排:有锚的插图排到所锚段落之后(与导出 docx 同位),真无锚才落文末。
  const anchoredBySection = useMemo(() => {
    const out: Record<string, typeof insertSlots> = {};
    for (const s of insertSlots) {
      if (s.suggestion.sectionId) (out[s.suggestion.sectionId] ??= []).push(s);
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slots, slotTasks]);
  const unanchoredSlots = insertSlots.filter((s) => !s.suggestion.sectionId);

  /* ── 品牌页脚:租户名 + 品牌资产联系方式(没有的字段不渲染,不编) ── */
  const brandQ = useBrandAssets();
  const brand = brandQ.data;
  const brandLogo = brand?.logos?.find((l) => l.isDefault) ?? brand?.logos?.[0];

  /* ── 交付 ──
     ★codex P3:取消确认框也算 mutation 成功 → 假「已开始下载」toast。返回真实结果,
     successToast 用函数按结果决定出不出。 */
  const runExport = async (format: "md" | "docx"): Promise<"exported" | "cancelled"> => {
    if (!pf || !article || pf.blocking.length > 0) return "cancelled"; // 按钮已禁,双保险
    let confirmFlag = false;
    if (pf.confirmation.length > 0) {
      const ok = await confirm({
        title: "导出前需要确认",
        description: `${pf.confirmation.map(humanizeReporterText).join(";")}。确认导出将绕过该门禁并落审计。`,
        confirmText: "确认导出",
      });
      if (!ok) return "cancelled";
      confirmFlag = true;
    }
    await reporterService.exportArticle(article.id, format, confirmFlag);
    return "exported";
  };

  const exportMut = useMutationWithToast({
    mutationFn: runExport,
    successToast: (outcome) => (outcome === "exported" ? "已开始下载" : false),
    errorToast: "导出失败",
  });

  /**
   * ★codex 2 轮 P1:复制也是交付路 —— blocking 时导出禁了、复制还亮着 = 同一道门旁边开着侧门。
   * 与导出同门:blocking 禁死(按钮层);confirmation 弹同一个确认框(纯前端复制无法落审计,
   * 如实告知「不落审计」而不是装作与导出等价)。
   */
  const copyMpMut = useMutationWithToast({
    mutationFn: async (): Promise<"copied" | "cancelled"> => {
      if (!pf || pf.blocking.length > 0) return "cancelled"; // 按钮已禁,双保险
      if (pf.confirmation.length > 0) {
        const ok = await confirm({
          title: "复制前需要确认",
          description: `${pf.confirmation.map(humanizeReporterText).join(";")}。复制走剪贴板不落导出审计,请确认内容状态后再发布。`,
          confirmText: "确认复制",
        });
        if (!ok) return "cancelled";
      }
      // 公众号格式 = 简单内联样式 HTML(标题/引言/小标题/正文),走剪贴板 text/html
      const html = buildMpHtml({
        title: article!.selectedTitle || titles[0] || "",
        summary: article!.summary ?? "",
        sections,
        tags,
      });
      const item = new ClipboardItem({
        "text/html": new Blob([html], { type: "text/html" }),
        "text/plain": new Blob([sections.map((s) => `${s.heading}\n${s.content}`).join("\n\n")], {
          type: "text/plain",
        }),
      });
      await navigator.clipboard.write([item]);
      return "copied";
    },
    successToast: (outcome) => (outcome === "copied" ? "已复制,去公众号编辑器直接粘贴" : false),
    errorToast: "复制失败(浏览器可能不允许剪贴板写入)",
  });

  if (draftQ.isLoading) return <CenteredLoader />;
  if (!draft || !article) {
    return (
      <div className="mx-auto max-w-3xl space-y-6 py-10 text-center">
        <p className="text-sm text-muted-foreground">这篇草稿还没有成稿,先回这篇的文章工作台。</p>
        <Button onClick={() => navigate(`/ai-jizhe/workbench/${draftId}`)}>回文章工作台</Button>
      </div>
    );
  }

  const title = article.selectedTitle || titles[0] || "未命名文章";
  const gatePass = quality?.gate === "PASS";
  const today = draft.updatedAt?.slice(0, 10) ?? "";

  return (
    <div className="w-full space-y-5">
      {/*
       * 0811 琳琳:「写完文章返回工作台,只是返回上一级写文的页面,还保留原来这篇的内容」。
       *
       * ★这条<b>不是路由错了</b>:从「成稿预览」往回一级本来就是这篇的「文章工作台」,
       *   和右边那行面包屑写的完全一致。错的是<b>按钮名</b> —— 全站的「返回工作台」指的是
       *   舱首页,这里却用同一个词表示「回到这篇的编辑页」。同一个词两个意思,
       *   谁点谁以为自己离开了这篇。改成面包屑里那个全称,别再和舱首页抢名字。
       * ★她真正想要的那个出口(离开这篇)此前<b>页面上根本没有</b> —— 补一颗。
       */}
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          data-reporter-back-workbench=""
          onClick={() => navigate(`/ai-jizhe/workbench/${draftId}`)}
        >
          <ArrowLeft className="mr-1.5 h-4 w-4" />
          回这篇的文章工作台
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="text-muted-foreground"
          data-reporter-back-hub=""
          onClick={() => navigate("/ai-jizhe")}
        >
          回新闻舱
        </Button>
        <span className="text-sm text-muted-foreground">
          新闻舱 / 文章工作台 / <b className="text-foreground">成稿预览</b>
        </span>
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_300px]">
        {/* 左:公众号级排版 */}
        <article
          className="mx-auto w-full max-w-[680px] overflow-hidden rounded-[calc(var(--radius)+4px)] border bg-card"
          data-reporter-preview=""
        >
          {coverUrl && (
            <div className="relative">
              <img src={coverUrl} alt="封面" className="aspect-video w-full object-cover" />
              <span className="absolute bottom-2 left-2 rounded-full bg-black/45 px-2 py-0.5 text-2xs text-white">
                封面 · 视觉舱生成 · 已计入 AIGC 标识
              </span>
            </div>
          )}
          <div className="px-6 py-6 sm:px-9">
            <h1 className="mb-3 text-xl font-bold leading-snug">{title}</h1>
            <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-2xs text-muted-foreground">
              {user?.tenantName && <span>{user.tenantName}</span>}
              <span>{today}</span>
              <span>
                {GENRE_LABEL[draft.genre] ?? draft.genre} · {totalWords} 字
              </span>
              {/* ★1011 起默认显示,受租户 compliance.visibleLabel 控制;"它管什么/不管什么"见 aigcLabelVisibility.ts。
                  ⛔ 别把封面那句「已计入 AIGC 标识」或导出产物也接到这个开关上:那些会离开平台,
                     属合规面,要老板单独拍。 */}
              {showAigcLabel && (
                <span className="rounded-full border border-border px-2 py-px">
                  {PREVIEW_AIGC_LABEL_TEXT}
                </span>
              )}
              {quality && (
                <span className={cn("font-bold", gatePass ? "text-success" : "text-warning")}>
                  质量门禁 {gateLabel(quality.gate)} · {quality.totalScore} 分
                </span>
              )}
            </div>
            {article.summary && (
              <p className="mb-5 border-l-2 border-primary/50 bg-muted/40 px-3.5 py-2.5 text-[13px] leading-relaxed text-muted-foreground">
                {article.summary}
              </p>
            )}
            {sections.map((s) => (
              <section key={s.id} className="mb-5">
                <h3 className="mb-2 text-[15px] font-bold">{s.heading}</h3>
                <p className="whitespace-pre-wrap text-[13.5px] leading-[1.9] text-foreground/90">
                  {s.content}
                </p>
                {/* 批二 P3:锚在本段的插图排段后 —— 预览所见 = docx 导出所得 */}
                {(anchoredBySection[s.id] ?? []).map((img) => (
                  <figure key={img.slot} className="mt-3">
                    <img
                      src={slotTasks[img.slot]!.coverThumbUrl!}
                      alt={img.suggestion.scene}
                      className="w-full rounded-lg border border-border"
                    />
                    <figcaption className="mt-1.5 text-center text-2xs text-muted-foreground">
                      {img.label} · 视觉舱生成:「{img.suggestion.scene}」
                      {img.suggestion.style ? ` · ${img.suggestion.style}` : ""}
                    </figcaption>
                  </figure>
                ))}
              </section>
            ))}
            {/* 真无锚的插图才落文末(与导出回落口径一致) */}
            {unanchoredSlots.map((s) => (
              <figure key={s.slot} className="mb-5">
                <img
                  src={slotTasks[s.slot]!.coverThumbUrl!}
                  alt={s.suggestion.scene}
                  className="w-full rounded-lg border border-border"
                />
                <figcaption className="mt-1.5 text-center text-2xs text-muted-foreground">
                  {s.label} · 视觉舱生成:「{s.suggestion.scene}」
                  {s.suggestion.style ? ` · ${s.suggestion.style}` : ""}
                </figcaption>
              </figure>
            ))}
            {tags.length > 0 && (
              <div className="mb-5 flex flex-wrap gap-1.5">
                {tags.map((t) => (
                  <span
                    key={t}
                    className="rounded-full bg-muted px-2.5 py-0.5 text-2xs text-muted-foreground"
                  >
                    #{t}
                  </span>
                ))}
              </div>
            )}
            {/* 品牌页脚:租户名 + 品牌资产联系方式;没配置的字段不渲染(不编) */}
            <div className="flex items-center gap-3 border-t border-border pt-4">
              {brandLogo?.signedUrl && (
                <img
                  src={brandLogo.signedUrl}
                  alt="logo"
                  className="h-9 w-9 rounded-md border border-border object-contain"
                />
              )}
              <div className="text-2xs leading-relaxed text-muted-foreground">
                {user?.tenantName && (
                  <b className="block text-[12px] text-foreground">{user.tenantName}</b>
                )}
                {[brand?.phone && `服务热线 ${brand.phone}`, brand?.email && `邮箱 ${brand.email}`]
                  .filter(Boolean)
                  .join(" · ") || "品牌联系方式可在「品牌资产」配置,配置后自动带入页脚"}
              </div>
            </div>
          </div>
        </article>

        {/* 右:预检 + 配图 + 交付 */}
        <aside className="flex h-fit flex-col gap-3.5 rounded-[calc(var(--radius)+4px)] border bg-card p-4">
          {preflightQ.isLoading ? (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              导出预检中…
            </div>
          ) : pf ? (
            <div
              data-reporter-preflight=""
              className={cn(
                "rounded-lg border px-3 py-2.5 text-2xs leading-relaxed",
                pf.blocking.length
                  ? "border-destructive/50 bg-destructive/5"
                  : pf.confirmation.length
                    ? "border-warning/50 bg-warning/5"
                    : "border-success/40 bg-success/5"
              )}
            >
              {pf.blocking.length > 0 ? (
                <>
                  <b className="text-destructive">导出被拦:</b>
                  {pf.blocking.map(humanizeReporterText).join(";")}(回这篇的文章工作台修完再来)
                </>
              ) : pf.confirmation.length > 0 ? (
                <>
                  <b className="text-warning">需确认后导出:</b>
                  {pf.confirmation.map(humanizeReporterText).join(";")}(确认即绕过门禁,落审计)
                </>
              ) : (
                <>
                  <Check className="mr-1 inline h-3.5 w-3.5 text-success" />
                  <b className="text-success">
                    {quality ? `${gateLabel(quality.gate)} · ${quality.totalScore} 分` : "预检通过"}
                  </b>{" "}
                  导出预检通过,无需确认项
                </>
              )}
              {pf.warning.length > 0 && (
                <div className="mt-1 text-muted-foreground">提示:{pf.warning.join(";")}</div>
              )}
            </div>
          ) : (
            <div className="rounded-lg border border-border px-3 py-2.5 text-2xs text-muted-foreground">
              预检没跑出来(刷新重试)。
            </div>
          )}

          {slots.length > 0 && (
            <div>
              <div className="mb-1.5 text-xs font-semibold">配图(视觉舱 · {slots.length} 张位)</div>
              <div className="flex gap-2">
                {slots.map((s) => {
                  const t = slotTasks[s.slot];
                  // E-12:图位要说清自己处于哪一态(生成中/失败/未生成),不能一律灰占位
                  const running = t && (t.status === "RUNNING" || t.status === "PENDING");
                  return (
                    <div
                      key={s.slot}
                      className="relative flex-1 overflow-hidden rounded-lg border border-border bg-muted/40"
                      style={{ aspectRatio: "1" }}
                    >
                      {t?.coverThumbUrl ? (
                        <img
                          src={t.coverThumbUrl}
                          alt={s.label}
                          className="h-full w-full object-cover"
                        />
                      ) : running ? (
                        <div className="grid h-full place-items-center text-2xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Loader2 className="h-3 w-3 animate-spin" />
                            生成中…
                          </span>
                        </div>
                      ) : t?.status === "FAILED" ? (
                        <div
                          className="grid h-full place-items-center px-1.5 text-center text-2xs text-destructive"
                          title={maskVendor(t.errorMessage) || "视觉舱任务失败"}
                        >
                          生成失败
                        </div>
                      ) : (
                        <div className="grid h-full place-items-center text-2xs text-muted-foreground">
                          未生成
                        </div>
                      )}
                      <span className="absolute bottom-1 left-1 rounded-full bg-black/45 px-1.5 py-px text-2xs text-white">
                        {s.label}
                      </span>
                    </div>
                  );
                })}
              </div>
              <p className="mt-1.5 text-2xs text-muted-foreground">
                生成 / 换一张在工作台「配图」tab;不满意可进视觉舱精修。
              </p>
            </div>
          )}

          <div>
            <div className="mb-1.5 text-xs font-semibold">交付</div>
            <div className="flex flex-col gap-2">
              <Button
                className="w-full"
                disabled={!pf || pf.blocking.length > 0 || exportMut.isPending}
                onClick={() => exportMut.mutate("docx")}
                data-reporter-export="docx"
              >
                <Download className="mr-2 h-4 w-4" />
                导出 Word
              </Button>
              <Button
                variant="outline"
                className="w-full"
                disabled={!pf || pf.blocking.length > 0 || exportMut.isPending}
                onClick={() => exportMut.mutate("md")}
                data-reporter-export="md"
              >
                <FileText className="mr-2 h-4 w-4" />
                导出 Markdown
              </Button>
              {/* 1009 新闻舱 × 官网:导出旁「发布到官网」+ 本文各站点的发布状态 */}
              <SitePublicationPanel
                articleId={article.id}
                genre={draft.genre}
                qualityGate={quality?.gate}
                preflight={pf}
                defaultSiteId={presetSiteId}
                draftId={draftId}
              />
              <Button
                variant="outline"
                className="w-full"
                disabled={!pf || pf.blocking.length > 0 || copyMpMut.isPending}
                onClick={() => copyMpMut.mutate(undefined)}
                data-reporter-copy-mp=""
              >
                <Copy className="mr-2 h-4 w-4" />
                复制公众号格式
              </Button>
              {/* S2-T2 已点亮:标题+分段文案带进长图搭建器(体裁边界:记者=文优先/长图=图优先,
                  种子只铺文字位,画面块在搭建器里补)。正文为空没得转,按钮如实禁用。 */}
              <Button
                variant="outline"
                className="w-full"
                disabled={sections.length === 0}
                title={
                  sections.length === 0 ? "正文为空,先生成文章" : "标题与分段文案带入长图搭建器"
                }
                data-reporter-to-longimage=""
                onClick={() =>
                  navigate("/ai-haibao/detail-long", {
                    state: {
                      scenePreset: {
                        kind: "longimage",
                        usage: "social",
                        reporterSeed: {
                          title,
                          paragraphs: sections.map((s) =>
                            s.heading ? `${s.heading}\n${s.content}` : s.content
                          ),
                        },
                      },
                    },
                  })
                }
              >
                转社交种草长图(视觉舱)
              </Button>
              {/* 批三「转渠道稿」:一稿多发的轻量实现 —— 原文+渠道指令新建一篇(体裁按渠道
                  映射),复用整条创建链;溯源 sourceDraftId 落库。照抄「转长图」handoff 先例。 */}
              <div className="mt-1 rounded-lg border border-border/60 bg-background/50 p-2.5">
                <div className="mb-1.5 text-2xs font-semibold">
                  转渠道稿
                  <span className="ml-1.5 font-normal text-muted-foreground">
                    以本文为素材按渠道写法重开一篇
                  </span>
                </div>
                <div className="flex gap-1.5">
                  {(["公众号", "知乎", "小红书"] as const).map((ch) => (
                    <Button
                      key={ch}
                      variant="outline"
                      size="sm"
                      className="flex-1 text-xs"
                      data-reporter-channel-variant={ch}
                      onClick={() =>
                        navigate("/ai-jizhe/create", {
                          state: {
                            scenePreset: {
                              kind: "reporter",
                              genre: reporterGenreOfChannel(ch),
                              sourceLabel: `转渠道稿·来自《${title}》`,
                              sourceDraftId: draftId,
                              materialSeed: {
                                title,
                                channel: ch,
                                body:
                                  `【改写指令】以下是已定稿的文章,请按${ch}的写法风格改写成一篇新稿,` +
                                  "事实与口径保持不变。\n\n" +
                                  sections.map((s) => `${s.heading}\n${s.content}`).join("\n\n"),
                              },
                            },
                          },
                        })
                      }
                    >
                      {ch}
                    </Button>
                  ))}
                </div>
              </div>
              <Button variant="outline" className="w-full opacity-60" disabled>
                <Newspaper className="mr-2 h-4 w-4" />
                发布中心 · 即将上线
              </Button>
            </div>
            <p className="mt-2 text-center text-2xs text-muted-foreground">
              Word 真嵌图、Markdown 带图链接(S2);导出与绕过门禁均落审计
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}

/** 公众号粘贴用 HTML(内联样式;编辑器会吃掉 class,只认 style)。 */
function buildMpHtml(a: {
  title: string;
  summary: string;
  sections: { heading: string; content: string }[];
  tags: string[];
}): string {
  const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const parts = [
    `<h1 style="font-size:20px;font-weight:700;line-height:1.4;">${esc(a.title)}</h1>`,
    a.summary
      ? `<blockquote style="border-left:3px solid #ccc;margin:12px 0;padding:8px 12px;color:#666;font-size:14px;line-height:1.8;">${esc(a.summary)}</blockquote>`
      : "",
    ...a.sections.map(
      (s) =>
        `<h3 style="font-size:16px;font-weight:700;margin:18px 0 8px;">${esc(s.heading)}</h3>` +
        s.content
          .split(/\n+/)
          .filter(Boolean)
          .map(
            (p) =>
              `<p style="font-size:15px;line-height:1.9;margin:0 0 12px;color:#333;">${esc(p)}</p>`
          )
          .join("")
    ),
    a.tags.length
      ? `<p style="color:#888;font-size:13px;">${a.tags.map((t) => `#${esc(t)}`).join(" ")}</p>`
      : "",
  ];
  return parts.filter(Boolean).join("");
}
